
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Loader2, Mail, Lock, ArrowRight, ArrowLeft, X, LogIn,
  Sparkles, Globe, ClipboardList, CheckCircle2, Send,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import JunagoLogo from "../components/JunagoLogo";
import Reveal, { StaggerReveal } from "../components/motion/Reveal";

const CONTACT_EMAIL = "hello@junago.io";
const BETA_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Demande d'accès bêta Junago")}`;

const FEATURES = [
  {
    icon: ClipboardList,
    title: "Flotte et réservations",
    desc: "Retrouvez vos véhicules, disponibilités, réservations et clients dans un espace centralisé.",
  },
  {
    icon: Sparkles,
    title: "Junago Copilot, votre IA intégrée",
    desc: "Un assistant intelligent pour vous accompagner dans la gestion quotidienne de votre activité.",
  },
  {
    icon: Globe,
    title: "Réservation en ligne",
    desc: "Présentez votre offre et recevez des réservations depuis une boutique connectée à votre activité.",
  },
];

// Bandeau photographique des véhicules
const VEHICLES = [
  { name: "Berline électrique", img: "https://images.unsplash.com/photo-1712885046114-5ea81a2f7555?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Coupé sport", img: "https://images.unsplash.com/photo-1573074556015-71d2140a6372?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "SUV premium", img: "https://images.unsplash.com/photo-1735620731955-b047a7122892?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Cabriolet vintage", img: "https://images.unsplash.com/photo-1531938082025-efdb69fbd925?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Roadster", img: "https://images.unsplash.com/photo-1567613074247-5e2564e1b90a?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Semi-rigide", img: "https://images.unsplash.com/photo-1593351415075-3bac9f45c877?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Vélo électrique", img: "https://images.unsplash.com/photo-1620802051782-725fa33db067?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
  { name: "Van aménagé", img: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&q=70&w=640&ixlib=rb-4.1.0" },
];

function VehicleMarquee() {
  const items = [...VEHICLES, ...VEHICLES];
  return (
    <div
      className="veh-mask relative w-full overflow-hidden select-none"
      data-testid="vehicle-marquee"
      aria-hidden="true"
    >
      <div className="veh-track flex w-max gap-4 px-4 sm:gap-5">
        {items.map((v, i) => (
          <figure
            key={i}
            className="relative h-40 w-60 shrink-0 overflow-hidden rounded-2xl border border-white/10 shadow-lg shadow-black/40 sm:h-44 sm:w-72"
            style={{ pointerEvents: "none" }}
          >
            <img
              src={v.img}
              alt=""
              loading="lazy"
              draggable="false"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
            <figcaption className="absolute bottom-3 left-3 flex items-center gap-2 text-sm font-semibold tracking-tight text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
              {v.name}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

// Mini-mockup 1 : calendrier
function CalendarMock() {
  const headers = ["L", "M", "M", "J", "V", "S", "D"];
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0B16]/80 p-3.5" data-testid="mock-calendar">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-white/90">Juin 2026</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Réservation confirmée
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {headers.map((d, i) => (
          <span key={`h${i}`} className="text-[8px] font-medium text-slate-500">{d}</span>
        ))}
        {Array.from({ length: 14 }).map((_, i) => {
          const n = i + 9;
          const inRange = n >= 11 && n <= 13;
          return (
            <span
              key={n}
              className={`py-1 text-[9px] ${
                inRange
                  ? `bg-violet-500 font-semibold text-white ${n === 11 ? "rounded-l-md" : ""} ${n === 13 ? "rounded-r-md" : ""}`
                  : "text-slate-400"
              }`}
            >
              {n}
            </span>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[9px] text-slate-400">
        <span className="h-1.5 w-1.5 rounded-full bg-violet-400" /> Tesla Model 3 · 11–13 juin
      </div>
    </div>
  );
}

// Mini-mockup 2 : copilote IA
function AiChatMock() {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-[#0B0B16]/80 p-3.5" data-testid="mock-ai-chat">
      <div className="self-end max-w-[86%] rounded-2xl rounded-br-sm bg-violet-500/90 px-3 py-1.5 text-[10px] leading-snug text-white">
        Est-ce que j'ai des impayés ?
      </div>
      <div className="flex items-start gap-1.5 self-start max-w-[94%]">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-violet-400/30 bg-violet-500/20">
          <Sparkles className="h-3 w-3 text-violet-300" />
        </span>
        <div className="rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] leading-relaxed text-slate-200">
          Oui, 2 factures restent à régler, pour un total de <span className="font-semibold text-white">480&nbsp;€</span>.
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
        <span className="flex-1 text-[9px] text-slate-500">Posez une question…</span>
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-500">
          <Send className="h-2.5 w-2.5 text-white" />
        </span>
      </div>
    </div>
  );
}

// Mini-mockup 3 : boutique
function ShopMock() {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0B16]/80 p-3.5" data-testid="mock-shop">
      <div className="overflow-hidden rounded-lg border border-white/10">
        <div className="flex items-center gap-1 bg-white/5 px-2 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
          <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
          <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
          <span className="ml-1.5 inline-flex items-center gap-1 text-[8px] text-slate-400">
            <Globe className="h-2.5 w-2.5" /> junago.io/ma-boutique
          </span>
        </div>
        <div className="p-2.5">
          <div className="mb-2 h-2 w-20 rounded bg-white/15" />
          <div className="grid grid-cols-3 gap-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-[4/3] rounded-md border border-white/5 bg-gradient-to-br from-violet-500/25 to-fuchsia-500/10" />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[10px]">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
        <span className="font-semibold text-emerald-300">Publiée en ligne</span>
      </div>
    </div>
  );
}

const MOCKUPS = [CalendarMock, AiChatMock, ShopMock];

// Authentification existante : logique et design conservés
function AuthModal({ open, onClose }) {
  const { login } = useAuth();
  const [view, setView] = useState("login");

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [fpEmail, setFpEmail] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);
  const [fpError, setFpError] = useState("");

  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setView("login");
    setForm({ email: "", password: "" });
    setError(""); setLoading(false);
    setFpEmail(""); setFpSent(false); setFpError(""); setFpLoading(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => { document.body.style.overflow = prev; clearTimeout(t); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      const dest = u.role === "vendor" ? "/vendor/pilotage" : "/mes-reservations";
      window.location.assign(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setFpError(""); setFpLoading(true);
    try {
      await api.post(`/auth/forgot-password`, { email: fpEmail });
      setFpSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setFpError(typeof d === "string" ? d : "Une erreur est survenue, réessayez.");
    } finally {
      setFpLoading(false);
    }
  };

  if (!open) return null;

  const inputCls =
    "w-full rounded-xl bg-[#0B0B16] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Connexion à Junago"
      data-testid="auth-modal"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-[fadeIn_200ms_ease]"
        onClick={onClose}
        aria-hidden="true"
        data-testid="auth-modal-backdrop"
      />

      <div
        className="relative w-full max-w-md rounded-3xl p-7 sm:p-8"
        style={{
          background: "linear-gradient(160deg, rgba(20,20,33,0.98), rgba(11,11,22,0.98))",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 40px 100px -30px rgba(124,58,237,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute -top-px left-10 right-10 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.7), transparent)" }}
        />
        <button
          onClick={onClose}
          className="absolute right-4 top-4 h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition"
          aria-label="Fermer"
          data-testid="auth-modal-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex justify-center">
          <JunagoLogo size={40} wordmark tone="light" />
        </div>

        {view === "login" ? (
          <>
            <h2 className="mt-6 text-center font-heading text-2xl font-medium tracking-tight text-white" data-testid="auth-modal-title">
              Bienvenue sur Junago
            </h2>
            <p className="mt-2 text-center text-sm text-slate-400">
              Connectez-vous à votre espace bêta privé.
            </p>

            <form onSubmit={submitLogin} className="mt-7 space-y-4" data-testid="login-form">
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400">Email</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    ref={firstFieldRef}
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    autoComplete="email"
                    className={inputCls}
                    placeholder="vous@exemple.com"
                    data-testid="login-email"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400">Mot de passe</label>
                  <button
                    type="button"
                    onClick={() => setView("forgot")}
                    className="text-xs font-medium text-violet-300 hover:text-violet-200 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    autoComplete="current-password"
                    className={inputCls}
                    placeholder="Votre mot de passe"
                    data-testid="login-password"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="group w-full h-12 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm text-white disabled:opacity-50 transition-transform hover:-translate-y-0.5"
                style={{ background: "linear-gradient(90deg, #7C3AED, #A855F7)", boxShadow: "0 14px 34px -12px rgba(124,58,237,0.65)" }}
                data-testid="login-submit"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Se connecter
                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-view">
            {fpSent ? (
              <div className="text-center" data-testid="forgot-success">
                <div className="mx-auto mt-6 h-12 w-12 rounded-2xl bg-emerald-500/10 border border-emerald-400/20 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <h2 className="mt-5 font-heading text-2xl font-medium tracking-tight text-white">Email envoyé</h2>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  Si un compte existe avec <strong className="text-slate-200">{fpEmail}</strong>, vous recevrez un lien de
                  réinitialisation valable 1 heure. Pensez à vérifier vos spams.
                </p>
                <button
                  onClick={() => setView("login")}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200 transition-colors"
                  data-testid="forgot-back-to-login"
                >
                  <ArrowLeft className="h-4 w-4" /> Retour à la connexion
                </button>
              </div>
            ) : (
              <>
                <h2 className="mt-6 text-center font-heading text-2xl font-medium tracking-tight text-white" data-testid="forgot-title">
                  Mot de passe oublié ?
                </h2>
                <p className="mt-2 text-center text-sm text-slate-400 leading-relaxed">
                  Entrez votre email : nous vous enverrons un lien pour choisir un nouveau mot de passe.
                </p>
                <form onSubmit={submitForgot} className="mt-7 space-y-4" data-testid="forgot-form">
                  <div>
                    <label className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400">Email</label>
                    <div className="mt-1.5 relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <input
                        ref={firstFieldRef}
                        type="email"
                        value={fpEmail}
                        onChange={(e) => setFpEmail(e.target.value)}
                        required
                        autoComplete="email"
                        className={inputCls}
                        placeholder="vous@exemple.com"
                        data-testid="forgot-email"
                      />
                    </div>
                  </div>
                  {fpError && (
                    <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="forgot-error">
                      {fpError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={fpLoading}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm text-white disabled:opacity-50 transition-transform hover:-translate-y-0.5"
                    style={{ background: "linear-gradient(90deg, #7C3AED, #A855F7)", boxShadow: "0 14px 34px -12px rgba(124,58,237,0.65)" }}
                    data-testid="forgot-submit"
                  >
                    {fpLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Envoyer le lien
                  </button>
                </form>
                <button
                  onClick={() => setView("login")}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                  data-testid="forgot-back"
                >
                  <ArrowLeft className="h-4 w-4" /> Retour à la connexion
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Nouvelle page Coming Soon
export default function ComingSoon() {
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = useCallback(() => setAuthOpen(true), []);
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  useEffect(() => {
    document.title = "Junago — La gestion de vos véhicules, réinventée.";
  }, []);

  return (
    <main
      data-testid="coming-soon-page"
      className="relative min-h-screen w-full overflow-x-hidden bg-[#070710] text-white"
    >
      {/* Fond décoratif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-64 left-1/2 h-[650px] w-[900px] -translate-x-1/2 rounded-full bg-violet-700/20 blur-[140px]" />
        <div className="absolute top-[680px] -right-48 h-[550px] w-[550px] rounded-full bg-fuchsia-600/[0.07] blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.4) 0.8px, transparent 0.8px)", backgroundSize: "34px 34px", maskImage: "linear-gradient(to bottom, black, transparent 65%)" }} />
      </div>

      {/* Header : logo original */}
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 pt-6 sm:px-8 sm:pt-8">
        <JunagoLogo size={38} wordmark tone="light" data-testid="landing-logo" />
        <button
          onClick={openAuth}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2.5 text-xs font-semibold text-white backdrop-blur-md transition hover:border-violet-300/40 hover:bg-white/[0.09] sm:px-5 sm:text-sm"
          data-testid="open-login-btn"
        >
          <LogIn className="h-4 w-4 text-violet-300" /> Se connecter
        </button>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-5xl px-5 pb-10 pt-20 text-center sm:px-8 sm:pt-28 md:pb-16" data-testid="landing-hero">
        <Reveal>
          <div className="mb-8 inline-flex max-w-full items-center gap-2 rounded-full border border-violet-300/20 bg-violet-400/[0.08] px-3.5 py-2 text-[11px] font-semibold tracking-wide text-violet-200 sm:text-xs">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-300" />
            <span>La plateforme de location nouvelle génération</span>
          </div>

          <h1
            className="mx-auto max-w-5xl font-heading text-[clamp(2.6rem,7.5vw,5.6rem)] font-semibold leading-[1.05] tracking-[-0.045em] text-white"
            data-testid="landing-title"
            aria-label="Toutes vos locations. Une seule plateforme."
          >
            <span aria-hidden="true">Toutes vos locations.</span>
            <br />
            <span className="text-gradient-animated">Une seule plateforme.</span>
          </h1>
        </Reveal>

        <Reveal delay={180}>
          <p className="mx-auto mt-7 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg md:text-xl">
            Junago réunit la gestion de vos véhicules, réservations, plannings,
            équipes et paiements dans un logiciel pensé pour les professionnels
            de la location, avec une intelligence artificielle intégrée.
          </p>
        </Reveal>

        <Reveal delay={280}>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-2 sm:gap-3" aria-label="Activités de location concernées">
            {["Voitures", "Motos", "Bateaux", "Vans", "Autres véhicules"].map((label) => (
              <span key={label} className="rounded-full border border-white/10 bg-white/[0.035] px-3.5 py-1.5 text-xs font-medium text-slate-300 sm:text-sm">
                {label}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={360}>
          <div className="mt-10 flex justify-center">
            <a
              href={BETA_MAILTO}
              className="btn-jn-light group inline-flex min-h-14 items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold shadow-[0_18px_60px_-22px_rgba(167,139,250,0.6)] transition-transform hover:-translate-y-0.5 sm:px-10"
              data-testid="request-beta-btn"
            >
              Demander un accès bêta
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={420}>
          <p className="mt-5 text-sm text-slate-500">
            Écrivez-nous également à{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-violet-300 transition-colors hover:text-violet-200" data-testid="landing-contact-email">
              {CONTACT_EMAIL}
            </a>
          </p>
        </Reveal>
      </section>

      {/* Bandeau véhicules existant */}
      <div className="relative z-10 pb-16 pt-5 md:pb-24" data-testid="landing-vehicle-band">
        <Reveal delay={160}>
          <VehicleMarquee />
        </Reveal>
      </div>

      {/* Fonctionnalités */}
      <section className="relative z-10 border-t border-white/[0.06] bg-white/[0.012]" data-testid="landing-features">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <div className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-violet-300">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                Tout votre métier, au même endroit
              </div>
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-white md:text-5xl" data-testid="features-title">
                Moins d'outils. <span className="text-gradient-animated">Plus de maîtrise.</span>
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-400 md:text-base">
                De la première demande de réservation jusqu'à l'encaissement,
                centralisez les opérations de votre activité de location.
              </p>
            </Reveal>
          </div>

          <StaggerReveal className="mt-14 grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3" step={110} y={26}>
            {FEATURES.map((f, i) => {
              const Mock = MOCKUPS[i];
              return (
                <div
                  key={f.title}
                  className="group relative flex h-full flex-col overflow-hidden rounded-[26px] border border-white/[0.10] bg-gradient-to-b from-white/[0.065] to-white/[0.018] p-6 shadow-[0_18px_60px_-38px_rgba(0,0,0,0.8)] transition-all duration-300 hover:-translate-y-1 hover:border-violet-400/35 hover:from-violet-400/[0.09] sm:p-7"
                  data-testid={`feature-card-${f.title.split(" ")[0].toLowerCase()}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-violet-300/15 bg-violet-400/10 transition-transform duration-300 group-hover:scale-105">
                    <f.icon className="h-5 w-5 text-violet-300" />
                  </div>
                  <div className="mt-6 font-heading text-xl font-semibold tracking-tight text-white">{f.title}</div>
                  <p className="mt-3 text-sm leading-7 text-slate-400">{f.desc}</p>
                  <Reveal className="mt-auto pt-7" delay={120 + i * 90} y={16}>
                    <Mock />
                  </Reveal>
                </div>
              );
            })}
          </StaggerReveal>

          <Reveal delay={150}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5 text-center">
              <span className="mr-1 text-xs font-semibold text-slate-500">Et aussi</span>
              {["Plannings", "Gestion des équipes", "Contrats", "Encaissements", "Suivi des clients"].map((item) => (
                <span key={item} className="rounded-full border border-white/[0.09] bg-white/[0.025] px-4 py-2 text-xs font-medium text-slate-300">
                  {item}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.07] bg-[#090914]" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 md:py-20">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <Reveal>
              <div>
                <JunagoLogo size={38} wordmark tone="light" />
                <p className="mt-5 max-w-md text-sm leading-7 text-slate-400" data-testid="footer-description">
                  Junago simplifie la gestion des activités de location. Véhicules,
                  réservations, clients, équipes, paiements et intelligence artificielle :
                  une plateforme pour piloter votre activité.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="relative overflow-hidden rounded-[26px] border border-violet-300/20 bg-gradient-to-br from-violet-500/[0.15] via-white/[0.035] to-fuchsia-500/[0.07] p-7 md:ml-auto md:max-w-md" data-testid="footer-beta-cta">
                <div aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-violet-500/20 blur-3xl" />
                <div className="relative font-heading text-xl font-semibold tracking-tight text-white">Découvrez Junago en avant-première.</div>
                <p className="relative mt-3 text-sm leading-6 text-slate-300">Demandez votre accès à la version bêta privée.</p>
                <a
                  href={BETA_MAILTO}
                  className="btn-jn-light group relative mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                  data-testid="footer-request-beta-btn"
                >
                  Demander un accès bêta
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <p className="relative mt-5 text-sm text-slate-400">
                  <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-violet-300 transition-colors hover:text-violet-200" data-testid="footer-contact-email">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-14 border-t border-white/[0.07] pt-6 text-xs text-slate-500" data-testid="footer-copyright">
            © 2026 Junago
          </div>
        </div>
      </footer>

      {/* Modale existante */}
      <AuthModal open={authOpen} onClose={closeAuth} />
    </main>
  );
}
